import { z } from "zod";

/**
 * Contratos do Funil Projetado: as metas do ano, mes a mes.
 *
 * So as ENTRADAS viajam e sao gravadas (o que a pessoa digita). Leads, vendas,
 * faturamento e custos projetados sao calculados a partir delas, num lugar so,
 * no front. Dinheiro em centavos; taxas como fracao (0,1 = 10%).
 */

export const MESES_NO_ANO = 12;
const centavos = z.number().int().min(0).max(100_000_000_00);
const taxa = z.number().min(0).max(1);

/** O que se digita em cada mes. Zero = "nao definido" (o calculo herda o mes anterior). */
export const entradasDoMesSchema = z
  .object({
    orcamentoCentavos: centavos,
    cplCentavos: centavos,
    /** Lead no CRM -> MQL. */
    taxaMql: taxa,
    /** MQL -> agendamento. */
    taxaAgendamento: taxa,
    /** Agendamento -> consulta realizada (proposta). */
    taxaConsulta: taxa,
    /** Consulta -> venda. */
    taxaVenda: taxa,
    ticketMedioCentavos: centavos,
    /** Faturamento que nao vem do marketing (indicacao, organico, retorno...). */
    faturamentoClinicaCentavos: centavos,
  })
  .strict();
export type EntradasDoMes = z.infer<typeof entradasDoMesSchema>;

export const ENTRADAS_VAZIAS: EntradasDoMes = {
  orcamentoCentavos: 0,
  cplCentavos: 0,
  taxaMql: 0,
  taxaAgendamento: 0,
  taxaConsulta: 0,
  taxaVenda: 0,
  ticketMedioCentavos: 0,
  faturamentoClinicaCentavos: 0,
};

export const anoDoFunilSchema = z.number().int().min(2020).max(2100);

/** Entradas do ano: escala de verba (vale para o ano todo) e os 12 meses. */
export const entradasDoAnoSchema = z
  .object({
    /** Aumento sobre o orcamento de todos os meses (0,2 = +20%). */
    escalaDeVerba: z.number().min(-1).max(10),
    meses: z.array(entradasDoMesSchema).length(MESES_NO_ANO),
  })
  .strict();
export type EntradasDoAno = z.infer<typeof entradasDoAnoSchema>;

export const funilProjetadoSchema = entradasDoAnoSchema.extend({
  ano: anoDoFunilSchema,
  atualizadoEm: z.iso.datetime(),
});
export type FunilProjetado = z.infer<typeof funilProjetadoSchema>;

export const listaDeFunisProjetadosSchema = z.object({ dados: z.array(funilProjetadoSchema) });
export type ListaDeFunisProjetados = z.infer<typeof listaDeFunisProjetadosSchema>;

export const criarFunilProjetadoSchema = z
  .object({
    ano: anoDoFunilSchema,
    /** Copia as entradas de outro ano ja cadastrado, como ponto de partida. */
    copiarDe: anoDoFunilSchema.optional(),
  })
  .strict();
export type CriarFunilProjetado = z.infer<typeof criarFunilProjetadoSchema>;

/** Realizado de um mes, vindo do CRM (e da midia, quando existir). */
export const realizadoDoMesSchema = z.object({
  mes: z.number().int().min(1).max(12),
  /** `null` ate Meta/Google existirem. */
  leadsGerenciador: z.number().int().min(0).nullable(),
  investimentoCentavos: z.number().int().min(0).nullable(),
  leadsCrm: z.number().int().min(0),
  mql: z.number().int().min(0),
  agendamentos: z.number().int().min(0),
  consultas: z.number().int().min(0),
  vendas: z.number().int().min(0),
  faturamentoCentavos: z.number().int().min(0),
  /** Vendas de origem marketing (Origem = "Midia Paga"). `null` sem o campo Origem. */
  faturamentoMarketingCentavos: z.number().int().min(0).nullable(),
});
export type RealizadoDoMes = z.infer<typeof realizadoDoMesSchema>;

export const realizadoDoAnoSchema = z.object({
  ano: anoDoFunilSchema,
  /** `false` sem Kommo conectado e mapeado: os meses vem zerados. */
  comCrm: z.boolean(),
  meses: z.array(realizadoDoMesSchema).length(MESES_NO_ANO),
});
export type RealizadoDoAno = z.infer<typeof realizadoDoAnoSchema>;
